import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Modal,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { productApi } from "../../api";
import { useNavigate } from "react-router-dom";

const GuestProduct = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Add effect to handle search
  useEffect(() => {
    const handleSearch = (event) => {
      const searchText = event.detail || "";
      if (searchText.trim() === "") {
        setFilteredProducts(products);
      } else {
        const filtered = products.filter(product =>
          product.title.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredProducts(filtered);
      }
    };

    // Listen for custom search event
    window.addEventListener("productSearch", handleSearch);
    return () => window.removeEventListener("productSearch", handleSearch);
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productApi.getAllProducts();
      
      // Log the raw response
      console.log("Raw API response:", response);
      
      // Check if response is valid
      if (!response) {
        console.error("No response received from API");
        return;
      }

      // Ensure response is an array
      const data = Array.isArray(response) ? response : [];
      console.log("Processed data:", data);

      // Sort products by ID and filter active products
      const sortedProducts = data
        .filter((product) => {
          if (!product) {
            console.warn("Found null/undefined product in data");
            return false;
          }
          console.log("Processing product:", {
            id: product.id,
            title: product.title,
            status: product.status
          });
          return product.status === true;
        })
        .sort((a, b) => a.id - b.id);
      
      console.log("Final filtered and sorted products:", sortedProducts);
      setProducts(sortedProducts);
      setFilteredProducts(sortedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      if (error.response) {
        console.error("Error response:", {
          status: error.response.status,
          data: error.response.data
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (product) => {
    setSelectedProduct(product);
    // Set the first available variation as default
    const firstVariation =
      product.prices && product.prices.length > 0 ? product.prices[0].name : "";
    setSelectedVariation(firstVariation);

    // Set images and select the first one by default
    const images = product.images && product.images.length > 0 ? product.images : [];
    setSelectedImages(images);
    setCurrentImageIndex(0);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
    setSelectedVariation("");
    setSelectedImages([]);
    setCurrentImageIndex(0);
  };

  const handleManageClick = () => {
    navigate("/products");
  };

  const getImageUrl = (image) => {
    if (!image) return "https://via.placeholder.com/150";
    if (Array.isArray(image)) {
      image = image[0]; // Take the first image if it's an array
    }
    if (!image) return "https://via.placeholder.com/150";
    if (typeof image === "string") {
      if (image.startsWith("data:")) return image;
      if (image.startsWith("http")) return image;
      return `${process.env.REACT_APP_API_URL}${image}`;
    }
    return "https://via.placeholder.com/150";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          justifyContent: isMobile ? "space-between" : "center",
          alignItems: "center",
          mb: 3,
          position: "relative",
        }}
      >
        <Typography variant="h4" align="center">
          Our Products
        </Typography>
        {isMobile && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleManageClick}
            sx={{ position: "absolute", right: 0 }}
          >
            Product Manage
          </Button>
        )}
      </Box>

      <Grid
        container
        spacing={3}
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          justifyContent: "center",
        }}
      >
        {filteredProducts.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <Card
              sx={{
                cursor: "pointer",
                width: "280px",
                display: "flex",
                flexDirection: "column",
                "&:hover": {
                  transform: "scale(1.02)",
                  transition: "transform 0.2s ease-in-out",
                },
              }}
              onClick={() => handleCardClick(product)}
            >
              <Box sx={{ width: "100%", paddingTop: "100%", position: "relative" }}>
                <CardMedia
                  component="img"
                  image={getImageUrl(product.images)}
                  alt={product.title}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" align="center" noWrap>
                  {product.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Modal
        open={Boolean(selectedProduct)}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: "80%",
            maxWidth: 1000,
            maxHeight: "90vh",
            overflow: "auto",
            display: "flex",
            gap: 4,
          }}
        >
          {selectedProduct && (
            <>
              <Box sx={{ flex: 1 }}>
                <Box
                  component="img"
                  src={getImageUrl([selectedImages[currentImageIndex]])}
                  alt="Product image"
                  sx={{
                    width: 300,
                    height: 300,
                    objectFit: "cover",
                    borderRadius: 2,
                    mb: 5,
                    display: "block",
                    margin: "0 auto",
                  }}
                />
                <Box display="flex" justifyContent="center" gap={0.1} flexWrap="wrap" mt={3}>
                  {selectedImages.map((img, idx) => (
                    <Box
                      key={idx}
                      component="img"
                      src={getImageUrl([img])}
                      onClick={() => setCurrentImageIndex(idx)}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: "cover",
                        borderRadius: 1,
                        border:
                          currentImageIndex === idx
                            ? "2px solid blue"
                            : "1px solid #ccc",
                        cursor: "pointer",
                        margin: 1,
                        transition: "border 0.3s ease",
                        "&:hover": {
                          border: "2px solid #007BFF",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" gutterBottom>
                  {selectedProduct.title}
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    mb: 3,
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    "& p": {
                      margin: "0 0 8px 0",
                    },
                    "& ul, & ol": {
                      margin: "0 0 8px 0",
                      paddingLeft: "20px",
                    },
                    "& li": {
                      margin: "4px 0",
                    },
                    "& h1, & h2, & h3, & h4, & h5, & h6": {
                      margin: "12px 0 8px 0",
                    },
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: selectedProduct.description || "",
                  }}
                />
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Select
                    value={selectedVariation}
                    onChange={(e) => setSelectedVariation(e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {selectedProduct.prices &&
                      selectedProduct.prices.map((price) => (
                        <MenuItem key={price.name} value={price.name}>
                          {price.name.charAt(0).toUpperCase() + price.name.slice(1)} - ${price.value}
                        </MenuItem>
                      ))}
                  </Select>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default GuestProduct;
